import React, {useState} from "react";
import {
    Button,
    CardMedia,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton, Radio,
    RadioGroup,
    Typography
} from "@mui/material";
import {
    AddShoppingCart,
    AttachMoney,
    CheckBox,
    List,
    Payment,
    Person,
    QrCodeScanner,
    ShoppingCart,
    ShoppingCartCheckout,
    PlayCircleFilledWhite,
    StopCircle
} from "@mui/icons-material";
import {CustomerServiceService} from "../customer-service/customer-service.service";
import {setAlert} from "../typescript/redux/actions";
import {useDispatch} from "react-redux";
import {SaleService} from "../sale/sale.service";
import {useNavigate} from "react-router-dom";
import {DeliveryQrCodeAction} from "../sale-after/delivery-qr-code-action";
import {Sale} from "../sale/sale";

const classes = {
    logo: {
        maxWidth: "400px", width: '100%', height: '300px', textAlign: 'center'
    },
    largeIcon: {
        fontSize: "100px"
    }
};

interface ShortcutsProps {
    checkoutOpened: boolean;
    customerServiceStarted: boolean;
    getDashboard: () => void;
}

const Shortcuts = ({checkoutOpened, customerServiceStarted, getDashboard}: ShortcutsProps) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('');

    const handleOpen = () => {
        setSelectedOption('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        CustomerServiceService.stop(selectedOption).then(() => {
            dispatch(setAlert({show: true, message: 'Atendimento finalizado com sucesso', severity: 'success'}));
            setOpen(false);
            getDashboard();
        }).catch((error) => {
            dispatch(setAlert({
                show: true,
                message: 'Erro ao finalizar atendimento - ' + error.response.data.message,
                severity: 'error'
            }));
        });
    };

    const renderAbrirCaixa = () => {
        return !checkoutOpened && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" href={'/checkout/open'}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <AttachMoney sx={classes.largeIcon}/>
                        <Typography>Abrir caixa</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const renderFecharCaixa = () => {
        return checkoutOpened && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" href={'/checkout/close'}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <AttachMoney sx={classes.largeIcon}/>
                        <Typography>Fechar caixa</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const renderDocuments = () => {
        return checkoutOpened && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" href={'/documents'}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <Payment sx={classes.largeIcon}/>
                        <Typography>Documentos</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const renderSales = () => {
        return checkoutOpened && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" href={'/sales'}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <ShoppingCart sx={classes.largeIcon}/>
                        <Typography>Vendas</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const renderSalesAdd = () => {
        return checkoutOpened && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" href={'/sale'}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <AddShoppingCart sx={classes.largeIcon}/>
                        <Typography>Nova venda</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const renderSalesAfter = () => {
        return checkoutOpened && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" href={'/sales/after'}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <ShoppingCartCheckout sx={classes.largeIcon}/>
                        <Typography>Pós vendas</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const canDeliver = (item: Sale) => {
        return !!item && item.advicedBy && !item.deliveredBy;
    }

    const deliverByQrCode = async (saleId: number) => {
        const sale = await SaleService.findOne(saleId);

        if (!canDeliver(sale)) {
            dispatch(setAlert({
                show: true,
                message: 'Não é possível entregar essa venda',
                severity: 'error'
            }));
            return;
        }

        return SaleService.delivery(saleId).then(() => {
            dispatch(setAlert({show: true, message: 'Venda entregue com sucesso', severity: 'success'}));
            navigate('/sales/after/' + saleId + '/receivables');
        }).catch((error) => {
            dispatch(setAlert({
                show: true,
                message: 'Erro ao entregar venda - ' + error.data.message,
                severity: 'error'
            }));
        });
    }

    const renderDeliveryQrCode = () => {
        return checkoutOpened && (
            <Grid item md xs={6}>
                <DeliveryQrCodeAction
                    onSubmitSaleId={deliverByQrCode}
                    onInvalidQrCode={() => dispatch(setAlert({
                        show: true,
                        message: 'QR Code inválido para entrega',
                        severity: 'error'
                    }))}
                    renderTrigger={(openScanner) => (
                        <IconButton color='primary' onClick={openScanner}>
                            <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                                <QrCodeScanner sx={classes.largeIcon}/>
                                <Typography>Entrega QR Code</Typography>
                            </Grid>
                        </IconButton>
                    )}
                />
            </Grid>
        )
    }

    const startCustomerService = () => {
        CustomerServiceService.start().then(() => {
            dispatch(setAlert({show: true, message: 'Atendimento iniciado com sucesso', severity: 'success'}));
            getDashboard();
        }).catch((error) => {
            dispatch(setAlert({
                show: true,
                message: 'Erro ao iniciar atendimento - ' + error.response.data.message,
                severity: 'error'
            }));
        })
    }

    const renderStartCustomerService = () => {
        return !customerServiceStarted && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" onClick={startCustomerService}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <PlayCircleFilledWhite sx={classes.largeIcon}/>
                        <Typography>Iniciar atendimento</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    const renderStopCustomerService = () => {
        return customerServiceStarted && (
            <Grid item md xs={6}>
                <IconButton color='primary' component="a" onClick={handleOpen}>
                    <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                        <StopCircle sx={classes.largeIcon}/>
                        <Typography>Finalizar atendimento</Typography>
                    </Grid>
                </IconButton>
            </Grid>
        )
    }

    return (
        <Grid container spacing={1} alignItems={"center"} style={{textAlign: "center", padding: '20px'}}>
            <Grid item md xs={12} container direction="column" alignItems="center" justifyContent="center">
                <CardMedia sx={classes.logo} image="/copacubana.png"/>
            </Grid>

            <Grid item md xs={12}>
                <Grid container justifyContent="space-between" alignItems="center">
                    {renderAbrirCaixa()}
                    {renderFecharCaixa()}
                    <Grid item md xs={6}>
                        <IconButton color='primary' component="a" href={'/activities'}>
                            <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                                <CheckBox sx={classes.largeIcon}/>
                                <Typography>Atividades</Typography>
                            </Grid>
                        </IconButton>
                    </Grid>
                    <Grid item md xs={6}>
                        <IconButton color='primary' component="a" href={'/persons'}>
                            <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                                <Person sx={classes.largeIcon}/>
                                <Typography>Pessoas</Typography>
                            </Grid>
                        </IconButton>
                    </Grid>
                    <Grid item md xs={6}>
                        <IconButton color='primary' component="a" href={'/products'}>
                            <Grid container direction='column' alignItems={"center"} style={{textAlign: "center"}}>
                                <List sx={classes.largeIcon}/>
                                <Typography>Produtos</Typography>
                            </Grid>
                        </IconButton>
                    </Grid>
                </Grid>

                <Grid container justifyContent="space-between" alignItems="center">
                    {renderSalesAdd()}
                    {renderSales()}
                    {renderSalesAfter()}
                    {renderDocuments()}
                </Grid>

                <Grid container justifyContent="space-between" alignItems="center">
                    {renderStartCustomerService()}
                    {renderStopCustomerService()}
                    {renderDeliveryQrCode()}
                </Grid>

            </Grid>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Finalizar Atendimento</DialogTitle>
                <DialogContent>
                    <RadioGroup value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
                        <FormControlLabel value="ADJUSTMENT" control={<Radio />} label="Ajuste"/>
                        <FormControlLabel value="SALE" control={<Radio />} label="Venda"/>
                        <FormControlLabel value="BUDGET" control={<Radio />} label="Orçamento"/>
                        <FormControlLabel value="CONSULTATION_INFO" control={<Radio />} label="Informações sobre consulta"/>
                        <FormControlLabel value="WITHDRAWAL" control={<Radio />} label="Retirada"/>
                        <FormControlLabel value="PAYMENT" control={<Radio />} label="Pagamento"/>
                        <FormControlLabel value="LENS_WARRANTY" control={<Radio />} label="Garantia lentes"/>
                        <FormControlLabel value="FRAME_WARRANTY" control={<Radio />} label="Garantia armação"/>
                        <FormControlLabel value="SUNGLASSES_WARRANTY" control={<Radio />} label="Garantia óculos solar"/>
                        <FormControlLabel value="JEWELRY_INFO" control={<Radio />} label="Informação sobre jóias"/>
                        <FormControlLabel value="OTHERS" control={<Radio />} label="Outros"/>
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleSave} disabled={!selectedOption}>Salvar</Button>
                </DialogActions>
            </Dialog>

        </Grid>
    )

}

export default React.memo(Shortcuts);
